import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../../../../firebase/config';
import emailChanges from '../../../../data/emailChanges';
import { timestampToDate, convertObjectDates } from '../../../../utils/firebase-timestamp-transformation';
import './GameTestingTab.scss';

const GameTestingTab = () => {
  const [loading, setLoading] = useState(false);
  const [testReports, setTestReports] = useState([]);
  const [migrated, setMigrated] = useState([]);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    requiresMigration: 0,
    migrated: 0,
    failed: 0
  });
  const [initialized, setInitialized] = useState(false);

  // Define timestamp fields to convert
  const timestampFields = ['createdAt', 'lastUpdated'];

  // Fetch game testing reports from Firestore
  const fetchTestReports = async () => {
    // Prevent multiple fetches if already loading
    if (loading) return;
    
    setLoading(true);
    setError(null);
    
    try {
      console.log('Fetching game testing reports...');
      
      // Access the testingReports collection directly
      const testingReportsRef = collection(db, 'gameTesting', 'testingReports', 'testingReports');
      const querySnapshot = await getDocs(testingReportsRef);
      
      console.log(`Found ${querySnapshot.size} game testing reports`);
      
      const testReportsData = [];
      querySnapshot.forEach((doc) => {
        // Convert timestamps to Date objects
        const data = convertObjectDates(doc.data(), timestampFields);
        
        testReportsData.push({
          id: doc.id,
          ...data,
        });
      });
      
      console.log('Processed game testing reports:', testReportsData.length);
      
      // Process the data to identify which reports need migration
      const processed = testReportsData.map(report => {
        // Check if assignedTo.email needs migration
        const needsAssignedToEmailMigration = report.assignedTo && report.assignedTo.email && 
          emailChanges.find(change => change.oldEmail === report.assignedTo.email);
        
        // Check if submittedBy.email needs migration
        const needsSubmittedByEmailMigration = report.submittedBy && report.submittedBy.email && 
          emailChanges.find(change => change.oldEmail === report.submittedBy.email);
        
        return {
          ...report,
          requiresMigration: Boolean(needsAssignedToEmailMigration || needsSubmittedByEmailMigration),
          assignedToEmailChange: needsAssignedToEmailMigration,
          submittedByEmailChange: needsSubmittedByEmailMigration,
          migrated: false,
          error: null
        };
      });
      
      // Update stats
      setStats({
        total: processed.length,
        requiresMigration: processed.filter(report => report.requiresMigration).length,
        migrated: 0,
        failed: 0
      });
      
      setTestReports(processed);
      setInitialized(true);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching game testing reports:', error);
      setError('Failed to fetch game testing reports: ' + error.message);
      setInitialized(true);
      setLoading(false);
    }
  };

  // Migrate a single test report
  const migrateTestReport = async (report) => {
    try {
      const reportRef = doc(db, 'gameTesting', 'testingReports', 'testingReports', report.id);
      const updateData = {};

      // Update assignedTo.email if needed
      if (report.assignedToEmailChange && report.assignedTo) {
        updateData['assignedTo.email'] = report.assignedToEmailChange.newEmail;
      }

      // Update submittedBy.email if needed
      if (report.submittedByEmailChange && report.submittedBy) {
        updateData['submittedBy.email'] = report.submittedByEmailChange.newEmail;
      }

      // Only update if there are changes to make
      if (Object.keys(updateData).length > 0) {
        await updateDoc(reportRef, updateData);
        return { success: true };
      } else {
        return { success: true, noChangesNeeded: true };
      }
    } catch (error) {
      console.error(`Error migrating test report ${report.id}:`, error);
      return { success: false, error: error.message };
    }
  };

  // Migrate all reports that require migration
  const migrateAllReports = async () => {
    if (!testReports || testReports.length === 0) return;
    
    setLoading(true);
    const updatedReports = [...testReports];
    let migratedCount = 0;
    let failedCount = 0;
    
    for (let i = 0; i < updatedReports.length; i++) {
      const report = updatedReports[i];
      
      if (report.requiresMigration && !report.migrated) {
        const result = await migrateTestReport(report);
        
        updatedReports[i] = {
          ...report,
          migrated: result.success,
          error: result.success ? null : result.error
        };
        
        if (result.success) {
          migratedCount++;
        } else {
          failedCount++;
        }
      }
    }
    
    // Update stats
    setStats(prev => ({
      ...prev,
      migrated: prev.migrated + migratedCount,
      failed: prev.failed + failedCount
    }));
    
    setTestReports(updatedReports);
    setMigrated(updatedReports.filter(report => report.migrated));
    setLoading(false);
  };

  // Format date timestamp for display
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestampToDate(timestamp);
    return date ? date.toLocaleString() : 'Invalid Date';
  };

  // Truncate long text for display
  const truncateText = (text, maxLength = 20) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Only run fetchTestReports once on component mount
  useEffect(() => {
    if (!initialized && !loading) {
      fetchTestReports();
    }
  }, [initialized, loading]);

  return (
    <div className="game-testing-tab tab-content-container">
      <div className="tab-header">
        <h2>Game Testing Reports Migration</h2>
        <p>Migrate email addresses in game testing reports from myelinh.com to myelinz.com domain.</p>
      </div>
      
      <div className="migration-stats">
        <div className="stat-item">
          <span className="stat-label">Total Reports:</span>
          <span className="stat-value">{stats.total}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Requires Migration:</span>
          <span className="stat-value">{stats.requiresMigration}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Migrated:</span>
          <span className="stat-value">{stats.migrated}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Failed:</span>
          <span className="stat-value">{stats.failed}</span>
        </div>
      </div>
      
      <div className="migration-actions">
        <button 
          className="fetch-btn" 
          onClick={fetchTestReports} 
          disabled={loading}
        >
          {loading ? 'Loading...' : 'Refresh Data'}
        </button>
        <button 
          className="migrate-btn" 
          onClick={migrateAllReports} 
          disabled={loading || stats.requiresMigration === 0 || stats.migrated === stats.requiresMigration}
        >
          {loading ? 'Migrating...' : 'Migrate All'}
        </button>
      </div>
      
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}
      
      <div className="data-table-container">
        <h3>Game Testing Reports Requiring Migration</h3>
        {loading ? (
          <div className="loading-indicator">Loading...</div>
        ) : (
          <>
            {testReports.length === 0 ? (
              <div className="no-data-message">No game testing reports found</div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Assigned To</th>
                    <th>New Assigned Email</th>
                    <th>Submitted By</th>
                    <th>New Submitter Email</th>
                    <th>Created At</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {testReports.filter(report => report.requiresMigration).map(report => (
                    <tr key={report.id} className={report.migrated ? 'migrated' : (report.error ? 'error' : '')}>
                      <td title={report.id}>{truncateText(report.id, 10)}</td>
                      <td title={report.assignedTo?.email || 'N/A'}>
                        {report.assignedTo?.email || 'N/A'}
                      </td>
                      <td title={report.assignedToEmailChange?.newEmail || 'No change'}>
                        {report.assignedToEmailChange ? report.assignedToEmailChange.newEmail : 'No change'}
                      </td>
                      <td title={report.submittedBy?.email || 'N/A'}>
                        {report.submittedBy?.email || 'N/A'}
                      </td>
                      <td title={report.submittedByEmailChange?.newEmail || 'No change'}>
                        {report.submittedByEmailChange ? report.submittedByEmailChange.newEmail : 'No change'}
                      </td>
                      <td>{formatDate(report.createdAt)}</td>
                      <td>
                        {report.migrated ? (
                          <span className="status-success">Migrated</span>
                        ) : report.error ? (
                          <span className="status-error" title={report.error}>Failed</span>
                        ) : (
                          <span className="status-pending">Pending</span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {testReports.filter(report => report.requiresMigration).length === 0 && (
                    <tr>
                      <td colSpan="7" className="no-data">No game testing reports require migration</td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </>
        )}
      </div>
      
      {migrated.length > 0 && (
        <div className="migration-results">
          <h3>Successfully Migrated Reports</h3>
          <div className="results-count">Total: {migrated.length}</div>
          <ul className="results-list">
            {migrated.map(report => (
              <li key={report.id}>
                {report.assignedToEmailChange && (
                  <div>
                    Assigned To: {report.assignedTo.email} → {report.assignedToEmailChange.newEmail}
                  </div>
                )}
                {report.submittedByEmailChange && (
                  <div>
                    Submitted By: {report.submittedBy.email} → {report.submittedByEmailChange.newEmail}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default GameTestingTab; 